"use client";

import { c as _c } from "react/compiler-runtime";
import { trapTabFocus } from "@/components/utils/modal/focusTrap";
import type { ApiWave } from "@/generated/models/ApiWave";
import { XMarkIcon } from "@heroicons/react/24/outline";
import { motion } from "framer-motion";
import { useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { WaveDropCreate } from "./WaveDropCreate";
interface WaveLeaderboardCurationDropModalProps {
  readonly isOpen: boolean;
  readonly wave: ApiWave;
  readonly onClose: () => void;
}
export function WaveLeaderboardCurationDropModal(t0) {
  const $ = _c(25);
  const {
    isOpen,
    wave,
    onClose
  } = t0;
  const canUseDOM = typeof document !== "undefined";
  const panelRef = useRef(null);
  const closeButtonRef = useRef(null);
  const titleRef = useRef(null);
  const onCloseRef = useRef(onClose);
  const previouslyFocusedElementRef = useRef(null);
  let t1;
  let t2;
  if ($[0] !== onClose) {
    t1 = () => {
      onCloseRef.current = onClose;
    };
    t2 = [onClose];
    $[0] = onClose;
    $[1] = t1;
    $[2] = t2;
  } else {
    t1 = $[1];
    t2 = $[2];
  }
  useEffect(t1, t2);
  let t3;
  let t4;
  if ($[3] !== isOpen) {
    t3 = () => {
      if (!isOpen) {
        return;
      }
      const activeElement = document.activeElement;
      previouslyFocusedElementRef.current = activeElement instanceof HTMLElement ? activeElement : null;
      const originalOverflow = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      const focusInitialTarget = () => {
        const initialTarget = closeButtonRef.current ?? titleRef.current ?? panelRef.current;
        initialTarget?.focus();
      };
      focusInitialTarget();
      const onKeyDown = event => {
        if (event.key === "Escape") {
          event.preventDefault();
          onCloseRef.current();
          return;
        }
        if (event.key !== "Tab") {
          return;
        }
        const panel = panelRef.current;
        if (!panel) {
          return;
        }
        trapTabFocus(event, panel);
      };
      document.addEventListener("keydown", onKeyDown);
      return () => {
        document.body.style.overflow = originalOverflow;
        document.removeEventListener("keydown", onKeyDown);
        const previouslyFocusedElement = previouslyFocusedElementRef.current;
        if (previouslyFocusedElement?.isConnected) {
          previouslyFocusedElement.focus();
        }
        previouslyFocusedElementRef.current = null;
      };
    };
    t4 = [isOpen];
    $[3] = isOpen;
    $[4] = t3;
    $[5] = t4;
  } else {
    t3 = $[4];
    t4 = $[5];
  }
  useEffect(t3, t4);
  if (!canUseDOM || !isOpen) {
    return null;
  }
  let t5;
  if ($[6] !== onClose) {
    t5 = <button type="button" onClick={onClose} className="tw-fixed tw-inset-0 tw-cursor-default tw-border-0 tw-bg-transparent tw-p-0" aria-label="Close drop artwork modal" tabIndex={-1} />;
    $[6] = onClose;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  let t6;
  let t7;
  let t8;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = {
      opacity: 0,
      y: 12,
      scale: 0.98
    };
    t7 = {
      opacity: 1,
      y: 0,
      scale: 1
    };
    t8 = {
      duration: 0.2,
      ease: "easeOut"
    };
    $[8] = t6;
    $[9] = t7;
    $[10] = t8;
  } else {
    t6 = $[8];
    t7 = $[9];
    t8 = $[10];
  }
  let t9;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = <h2 id="leaderboard-drop-art-title" ref={titleRef} className="tw-mb-0 tw-text-2xl tw-font-bold tw-text-white" tabIndex={-1}>Drop Artwork</h2>;
    $[11] = t9;
  } else {
    t9 = $[11];
  }
  let t10;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = <XMarkIcon className="tw-size-6 tw-flex-shrink-0" />;
    $[12] = t10;
  } else {
    t10 = $[12];
  }
  let t11;
  if ($[13] !== onClose) {
    t11 = <div className="tw-flex tw-items-center tw-justify-between tw-gap-4 tw-border-b tw-border-solid tw-border-iron-800 tw-p-6">{t9}<button ref={closeButtonRef} type="button" onClick={onClose} className="tw-flex tw-size-9 tw-flex-shrink-0 tw-items-center tw-justify-center tw-rounded-full tw-border-0 tw-bg-transparent tw-text-iron-300 tw-ring-1 tw-ring-inset tw-ring-iron-700 tw-transition tw-duration-300 tw-ease-out desktop-hover:hover:tw-text-iron-100" aria-label="Close modal">{t10}</button></div>;
    $[13] = onClose;
    $[14] = t11;
  } else {
    t11 = $[14];
  }
  let t12;
  if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = <p className="tw-mb-5 tw-max-w-lg tw-text-base tw-text-iron-400">Enter a supported curation URL to submit a new piece to the leaderboard.</p>;
    $[15] = t12;
  } else {
    t12 = $[15];
  }
  let t13;
  if ($[16] !== onClose || $[17] !== wave) {
    t13 = <div className="tw-max-h-[calc(90vh-88px)] tw-overflow-y-auto tw-scrollbar-thin tw-scrollbar-track-iron-800 tw-scrollbar-thumb-iron-500 hover:tw-scrollbar-thumb-iron-300"><div className="tw-px-6 tw-pb-6 tw-pt-5">{t12}<WaveDropCreate wave={wave} onSuccess={onClose} isCurationLeaderboard={true} /></div></div>;
    $[16] = onClose;
    $[17] = wave;
    $[18] = t13;
  } else {
    t13 = $[18];
  }
  let t14;
  if ($[19] !== t11 || $[20] !== t13) {
    t14 = <div className="tw-relative tw-z-10 tw-flex tw-h-full tw-items-start tw-justify-center tw-px-4 tw-pb-4 tw-pt-[calc(env(safe-area-inset-top,0px)+1rem)] lg:tw-items-center"><motion.div ref={panelRef} initial={t6} animate={t7} transition={t8} className="tw-max-h-[90vh] tw-w-full tw-max-w-3xl tw-overflow-hidden tw-rounded-xl tw-border tw-border-solid tw-border-iron-800 tw-bg-iron-950 tw-shadow-2xl" data-testid="curation-drop-modal-panel" tabIndex={-1}>{t11}{t13}</motion.div></div>;
    $[19] = t11;
    $[20] = t13;
    $[21] = t14;
  } else {
    t14 = $[21];
  }
  let t15;
  if ($[22] !== t14 || $[23] !== t5) {
    t15 = createPortal(<dialog open={true} aria-modal="true" aria-labelledby="leaderboard-drop-art-title" className="tailwind-scope tw-fixed tw-inset-0 tw-z-[1000] tw-m-0 tw-h-full tw-w-full tw-max-w-none tw-border-0 tw-bg-gray-600/50 tw-p-0 tw-outline-none tw-backdrop-blur-[1px]" data-testid="curation-drop-modal">{t5}{t14}</dialog>, document.body);
    $[22] = t14;
    $[23] = t5;
    $[24] = t15;
  } else {
    t15 = $[24];
  }
  return t15;
}